import React, { useState, useContext, useEffect } from 'react';
import { Box, List, Chip, Stack, Typography, Divider, CircularProgress, Grid, Button, Link } from '@mui/material';
import { WorkflowVerificationContext } from '../../services/workflowVerificationService';
import LabelStack from '../../Components/LabelStack';
import { CLAIM_NAMES, CLAIM_TYPE, FILE_VSKUS, VSKU_NAMES } from './verificationNames';

const REGULATORY_ACTION_FIELDS = [
  'issuingAgencyName',
  'dataFreshness',
  'entityName',
  'assessment_result',
  'assessment',
  'actionDisposition',
  'actionType',
  'actionDate',
  'caseReference',
  'penaltyAmount',
  'matchedOn',
  'sourceUrl',
  'documentUrl',
];

const REGULATORY_ACTION_FIELD_LABELS = {
  issuingAgencyName: 'Issuing Agency Name',
  dataFreshness: 'Data Freshness',
  entityName: 'Entity Name',
  assessment_result: 'Assessment Result',
  assessment: 'Assessment',
  actionDisposition: 'Action Disposition',
  actionType: 'Action Type',
  actionDate: 'Action Date',
  caseReference: 'Case Reference',
  penaltyAmount: 'Penalty Amount',
  matchedOn: 'Matched On',
  sourceUrl: 'Source URL',
  documentUrl: 'Document URL',
};

const getRegulatoryActionFieldValue = ({ action, fieldName }) => {
  if (fieldName === 'assessment_result') {
    return action.assessment_result ?? action.assessmentResult
  }

  return action[fieldName]
}

const formatIndividualContactValue = (value) => {
  if (typeof value === 'string') {
    return value
  }
  if (!value || typeof value !== 'object') {
    return String(value)
  }

  const individualName = typeof value.individualName === 'string' ? value.individualName : ''
  const individualEmailAddress = typeof value.individualEmailAddress === 'string' ? value.individualEmailAddress : ''

  if (!individualName) {
    if (individualEmailAddress) {
      return individualEmailAddress
    }
    return JSON.stringify(value)
  }

  return individualEmailAddress ? `${individualName} <${individualEmailAddress}>` : individualName
}

const formatValue = ({ name, value }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-'
    }
    if (name === 'individualContact') {
      return value.map(formatIndividualContactValue).join(', ')
    }
    return value.join(', ')
  }
  if(CLAIM_TYPE[name] === 'date') {
    if (!value) {
      return '-'
    }
    const date = new Date(parseInt(value, 10))
    return date.toLocaleDateString()
  }
  return value
}

const parseRegulatoryActions = (value) => {
  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

const RegulatoryActionsField = ({ value }) => {
  const actions = parseRegulatoryActions(value)

  if (actions.length === 0) {
    if (value?.trim?.() === '[]') {
      return <Typography textAlign='left'>No matched regulatory actions</Typography>
    }

    return <Typography textAlign='left'>{value || '-'}</Typography>
  }

  return (
    <Stack spacing='1rem'>
      {actions.map((action, index) => (
        <Box
          key={`regulatory-action-${index}`}
          sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '0.5rem', padding: '0.75rem' }}
        >
          <Typography textAlign='left' variant='subtitle2' sx={{ marginBottom: '0.5rem' }}>
            Regulatory Action {index + 1}
          </Typography>
          <Grid container rowSpacing={0.75}>
            {REGULATORY_ACTION_FIELDS.map((fieldName) => {
              const fieldValue = getRegulatoryActionFieldValue({ action, fieldName })
              const displayValue = Array.isArray(fieldValue) ? fieldValue.join(', ') : String(fieldValue)
              if (
                fieldValue === undefined
                || fieldValue === null
                || fieldValue === ''
                || (Array.isArray(fieldValue) && fieldValue.length === 0)
              ) {
                return null
              }

              return (
                <React.Fragment key={`${fieldName}-${index}`}>
                  <Grid size={5}>
                    <Typography textAlign='left' variant='body2'>
                      {REGULATORY_ACTION_FIELD_LABELS[fieldName]}
                    </Typography>
                  </Grid>
                  <Grid size={7}>
                    {fieldName === 'sourceUrl' || fieldName === 'documentUrl'
                      ? (
                        <Link
                          href={fieldValue}
                          rel='noopener noreferrer'
                          target='_blank'
                          textAlign='left'
                        >
                          {displayValue}
                        </Link>
                      )
                      : <Typography textAlign='left' variant='body2'>{displayValue}</Typography>}
                  </Grid>
                </React.Fragment>
              )
            })}
          </Grid>
        </Box>
      ))}
    </Stack>
  )
}

const renderFieldValue = (result) => {
  if (result.name === 'regulatoryActions') {
    return <RegulatoryActionsField value={result.value} />
  }

  return <Typography textAlign='left'>{formatValue(result)}</Typography>
}

const getFields = ({ claims, results }) => {
  if (results && results.length > 0) {
    return results
  }
  if (claims && claims.length > 0) {
    return claims
  }
  return []
}

const formatCreationTime = (timestamp) => {
  if (!timestamp) return null
  const date = new Date(Number(timestamp))
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const VerificationOrderItem = ({ item, index }) => {
  const { vsku, labels } = item
  const fields = getFields(item)
  if (fields.some(({ name }) => name === 'documentId') && FILE_VSKUS.includes(vsku)) {
    const fieldMap = Object.fromEntries(fields.map(({ name, value }) => ([name, value])))
    return (
      <Box key={`order-item-${index}`}>
        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='h5'>{VSKU_NAMES[vsku] || vsku}</Typography> 
          <LabelStack labels={labels} />
        </Stack>
        <Grid container sx={{ backgroundColor: 'rgba(245, 245, 245, 0.60)', padding: '1rem', textAlign: 'left' }}>
          <React.Fragment key={`order-item-1`}>
            <Grid size={6}>
              <Typography textAlign='left' variant='subtitle1'>{CLAIM_NAMES['fileName']}</Typography>
            </Grid>
            <Grid size={6}>
              <Link href={fieldMap['documentId']} textAlign='left'>
                {fieldMap['fileName']}
              </Link>
            </Grid>
          </React.Fragment>
        </Grid>
      </Box>
    )
  }

  return (
    <Box key={`order-item-${index}`}>
      <Stack direction='row' justifyContent='space-between'>
        <Typography variant='h5'>{VSKU_NAMES[vsku] || vsku}</Typography> 
        <LabelStack labels={labels} />
      </Stack>
      
      <Grid container sx={{ backgroundColor: 'rgba(245, 245, 245, 0.60)', padding: '1rem', textAlign: 'left' }}>
        {fields.map((result, idx) => 
        <React.Fragment key={`order-item-${idx}`}>
          <Grid size={6}>
            <Typography textAlign='left' variant='subtitle1'>{CLAIM_NAMES[result.name] || result.name}</Typography>
          </Grid>
          <Grid size={6}>
            {renderFieldValue(result)}
          </Grid>
        </React.Fragment>,
        )}
      </Grid>
    </Box>
    
  )
}

export const VerificationSidePanel = ({ orderId }) => {
  const { getWorkflowVerification } = useContext(WorkflowVerificationContext)
  const [orderDetails, setOrderDetails] = useState()
  useEffect(() => {
    const retrieveDetails = async () => {
      if (orderId) {
        const details = await getWorkflowVerification({ orderId })
        setOrderDetails(details)
      }
    }
    retrieveDetails()
  }, [getWorkflowVerification, orderId])
  if (!orderDetails) {
    return (
      <Box sx={{ padding: '2rem', height: '100%', minWidth: '40vw' }}>
        <CircularProgress />
      </Box>
    )
  }

  const { title, businessId, labels, jurisdictions, items, orderStatus, orderCreationTime } = orderDetails || {}
  const orderAttributes = [
    { label: 'Order ID:', value: orderId },
    { label: 'Business ID:', value: businessId },
    { label: 'Status:', value: orderStatus },
    { label: 'Created:', value: formatCreationTime(orderCreationTime) },
  ].filter(({ value }) => value)
  return (
    <Box sx={{ padding: '2rem', height: '100%', width: '40vw' }}>
        <Stack direction='column' justifyContent='space-between' height='100%'>
        <Box>
          <Stack direction='row' sx={{ alignItems: 'end' }} spacing='0.25rem'>
            <Typography variant='h5' color='emphasis.main'>{title}</Typography>
            <Typography variant='h6' color='emphasis.light' lineHeight='1.5'>{['', ...jurisdictions].join(' / ')}</Typography>
          </Stack>
          <Box sx={{ backgroundColor: 'rgba(245, 245, 245, 0.60)' }} marginY='1rem' padding='1rem' >
            {orderAttributes.map(({ label, value }, index) => 
              <Stack key={`attribute-${index}`} direction='row' justifyContent='space-between' spacing='0.25rem'>
                <Typography variant='subtitle2'>{label}</Typography>
                <Typography variant='subtitle2'>{value}</Typography>
              </Stack>,
            )}
          </Box>
          <LabelStack labels={labels} />
          <Divider sx={{ marginTop: '1.5rem', marginBottom: '1.5rem' }} />
          <List>
            {items.map((item, index) => <VerificationOrderItem item={item} index={index} />)}
          </List>
          
        </Box>
        {/* Disable this button until we have this functionality
        <Box>
          <Button variant='outlined'>Cancel Order</Button>
        </Box> */}
      </Stack>
        
      
    </Box>
    
  )
}
